// Copyright 2016 Sorama B.V.

import { autoinject, bindable, BindingEngine, bindingMode, computedFrom, Disposable } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { Point2D } from "datatypes/SoramaLocalApi";
import { IExportable } from "libraries/Export/IExportable";
import { RangeLogic } from "libraries/Math/RangeLogic";
import { SVGNode } from "libraries/SVG/SVGNode";
import { SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { Generic } from "units/Generic";
import { Unit } from "units/Unit";
import { PlotData } from "../graph-plot/GraphPlot";

abstract class RangeRectangleHeader {
    public readonly _path: string;
    public readonly _textX: number;
    public readonly _textY: number;
    public readonly _removeX: number;
    public readonly _removeY: number;

    public value: string;
    public x: number;
    public y: number;
    public width: number;
    public height: number;

    public get size(): number {
        return BinSelectionPart.LabelHeight;
    }
    public get path(): string {
        return this._path;
    }
    public get textX(): number {
        return this._textX;
    }
    public get textY(): number {
        return this._textY;
    }
    public get removeX(): number {
        return this._removeX;
    }

    public get removeY(): number {
        return this._removeY;
    }

    public get endX(): number {
        return this.x + (this.width - BinSelectionPart.LabelHeight);
    }

    public rectangle: RangeRectangle;

    constructor(x: number, y: number, width: number, height: number, rectangle: RangeRectangle, value: string) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.value = value;
        this.rectangle = rectangle;
    }
}

const offset = 5;

class RangeRectangleHeaderX extends RangeRectangleHeader {
    public flipped: boolean;
    public toHeader: boolean;

    public get path(): string {
        // This makes the following code a little less verbose
        const { x, y, width, height, endX } = this;

        if (!this.toHeader) {
            if (!this.flipped) {
                return [
                    `${x},${y - offset}`,
                    `${x + offset},${y}`,
                    `${endX},${y}`,
                    `${endX},${y + height}`,
                    `${x},${y + height}`,
                ].join(" ");
            } else {
                return [
                    `${x},${y}`,
                    `${endX - offset},${y}`,
                    `${endX},${y - offset}`,
                    `${endX},${y + height}`,
                    `${x},${y + height}`,
                ].join(" ");
            }
        } else if (!this.flipped) {
            return [
                `${x},${y}`,
                `${x + width},${y}`,
                `${x + width},${y + height + offset}`,
                `${x + width - offset},${y + height}`,
                `${x},${y + height}`,
            ].join(" ");
        } else {
            return [
                `${x},${y}`,
                `${x + width},${y}`,
                `${x + width},${y + height}`,
                `${x + offset},${y + height}`,
                `${x},${y + height + offset}`,
            ].join(" ");
        }
    }

    public get textX(): number {
        if (!this.toHeader) {
            return this.x + (this.width - BinSelectionPart.LabelHeight) * 0.5;
        } else {
            return this.x + (this.width - BinSelectionPart.LabelHeight) * 0.5 + BinSelectionPart.LabelHeight - 3;
        }
    }

    public get textY(): number {
        return this.y + this.height * 0.5 + 3;
    }

    public get removeX(): number {
        if (!this.toHeader) {
            return this.x + this.width - BinSelectionPart.LabelHeight * 0.5;
        } else {
            return this.x + BinSelectionPart.LabelHeight * 0.5;
        }
    }

    public get removeY(): number {
        return this.y + this.height * 0.5;
    }

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        toHeader: boolean,
        flipped: boolean,
        rectangle: RangeRectangle,
        value: string
    ) {
        super(x, y, width, height, rectangle, value);

        this.toHeader = toHeader;
        this.flipped = flipped;
    }
}

class RangeRectangleHeaderY extends RangeRectangleHeader {
    public flipped: boolean;
    public toHeader: boolean;

    public get path(): string {
        const { x, y, width, height, endX } = this;

        if (!this.toHeader) {
            if (!this.flipped) {
                return [
                    `${x},${y}`,
                    `${endX - offset},${y}`,
                    `${endX + height - offset},${y + height}`,
                    `${x},${y + height}`,
                ].join(" ");
            } else {
                return [
                    `${x},${y}`,
                    `${endX + height - offset},${y}`,
                    `${endX - offset},${y + height}`,
                    `${x},${y + height}`,
                ].join(" ");
            }
        } else if (!this.flipped) {
            return [
                `${x - height + offset},${y}`,
                `${x + width},${y}`,
                `${x + width},${y + height}`,
                `${x + offset},${y + height}`,
            ].join(" ");
        } else {
            return [
                `${x + offset},${y}`,
                `${x + width},${y}`,
                `${x + width},${y + height}`,
                `${x - height + offset},${y + height}`,
            ].join(" ");
        }
    }

    public get textX(): number {
        if (!this.toHeader) {
            return this.x + (this.width - BinSelectionPart.LabelHeight) * 0.5;
        } else {
            return this.x + (this.width - BinSelectionPart.LabelHeight) * 0.5 + BinSelectionPart.LabelHeight - 3;
        }
    }

    public get textY(): number {
        return this.y + this.height * 0.5 + 3;
    }

    public get removeX(): number {
        if (!this.toHeader) {
            return this.x + this.width - BinSelectionPart.LabelHeight * 0.5;
        } else {
            return this.x + BinSelectionPart.LabelHeight * 0.5;
        }
    }

    public get removeY(): number {
        return this.y + this.height * 0.5;
    }

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        toHeader: boolean,
        flipped: boolean,
        rectangle: RangeRectangle,
        value: string
    ) {
        super(x, y, width, height, rectangle, value);

        this.toHeader = toHeader;
        this.flipped = flipped;
    }
}

class RangeRectangle {
    range: SelectableRange;
    x0: number;
    y0: number;
    width: number;
    height: number;

    // Properties for editing
    isEditingFromValue = false;
    isEditingToValue = false;
    newFrom: number;
    newTo: number;

    constructor(range: SelectableRange, x0: number, y0: number, width: number, height: number) {
        this.range = range;
        this.x0 = x0;
        this.y0 = y0;
        this.width = width;
        this.height = height;
    }
}

class RangeLine {
    value: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

type LabelCircle = {
    circlePos: Point2D;
    labelValue: number;
};

@autoinject
export class BinSelectionPart extends PlotPartBase implements IExportable {
    private plotElement: SVGElement;

    public static LabelHeight = 19;
    public static LabelMargin = 7;
    public static LabelWidth: number = 47 + BinSelectionPart.LabelHeight;
    public static MaxStackedLabels = 4;

    private get labelHeightWithMargin(): number {
        return BinSelectionPart.LabelHeight + BinSelectionPart.LabelMargin;
    }
    private get labelWidthWithMargin(): number {
        return BinSelectionPart.LabelWidth + BinSelectionPart.LabelMargin;
    }
    private get maxStackedHeight(): number {
        return BinSelectionPart.MaxStackedLabels * this.labelHeightWithMargin;
    }
    private get maxStackedWidth(): number {
        return BinSelectionPart.MaxStackedLabels * this.labelWidthWithMargin;
    }

    static instanceCounter = 0;
    private readonly instanceNumber: number;

    private readonly bindingEngine: BindingEngine;

    //#region Bindables
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public readonly selection: SelectableRange[];

    /**
     * Callback function that is called when any of the ranges are changed, selected or deselected
     */
    @bindable public selectionUpdatedCallback: (selection: SelectableRange[]) => void = () => {};

    @bindable public selectionmode = "y";
    @bindable public selectorfill = "black";
    @bindable public selectedfill = "black";
    @bindable public selectorstroke = "black";
    @bindable public selectorfillopacity = 1.0;
    @bindable public selectorstrokeopacity = 1.0;
    @bindable public steppedselectorcolor = "white";
    @bindable public steppedselectoropacity = "0.6";
    @bindable public selectionunit: Unit = Generic.unitless;
    @bindable public selectable = false;
    @bindable public singleSelection = false;
    @bindable public blockMouseEvents = false;
    @bindable public allowMultipleActiveRanges = false;
    @bindable public stepped = false;
    @bindable public pathData: PlotData[] = [];
    @bindable public hoverAxis: "x" | "y" = "x"; // default
    //#endregion

    //#region Fields
    private useBaseUnit = false;
    private _selectionunit: Unit;

    private viewBox = "0 0 1 1";
    private mouseOver: boolean;
    private mouseDown: boolean;
    private touchActive = false;
    private selectionHasChanged = false;

    private resizingPart: "lower" | "center" | "upper";

    private selectionBorderOffset = 10;

    private lastMouseX = 0;
    private lastMouseY = 0;

    private ranges: SelectableRange[] = [];
    private currentRange: SelectableRange;
    private currentValue = 0;
    private currentCursor = "auto";

    private rectangles: RangeRectangle[] = [];

    // The lines shown when the user hovers over the graph (selector)
    private lines: RangeLine[] = [];

    // The circles together with values shown where lines intersect the graph
    private labelCircles: LabelCircle[] = [];

    @computedFrom("instanceNumber")
    private get clipPathId(): string {
        return `binselection_clippath_${this.instanceNumber}`;
    }

    @computedFrom("rectangles", "actualSize", "viewPort")
    private get rectanglesHeaders(): RangeRectangleHeader[] {
        if (this.isSelectionModeX) return this.getXSelectionHeaders();

        return this.getYSelectionHeaders();
    }

    private valueBackgroundWidth = 50;

    //#endregion

    private selectedRangesDisposable: Disposable;

    private selectionDisposable: Disposable;

    //#region Computed Properties
    public get minValue(): number {
        if (this.selectionmode === "x") {
            return this._selectionunit.fromUnit(this.sourceUnits.x, this.viewPort.x);
        }

        if (this.selectionmode === "y") {
            return this._selectionunit.fromUnit(this.sourceUnits.y, this.viewPort.y);
        }
    }

    public get maxValue(): number {
        if (this.selectionmode === "x") {
            return this._selectionunit.fromUnit(this.sourceUnits.x, this.viewPort.x + this.viewPort.width);
        }

        if (this.selectionmode === "y") {
            return this._selectionunit.fromUnit(this.sourceUnits.y, this.viewPort.y + this.viewPort.height);
        }
    }
    //#endregion

    //#region Constructor and Initialization

    constructor(bindingEngine: BindingEngine) {
        super(0);
        this.instanceNumber = BinSelectionPart.instanceCounter++;
        this.bindingEngine = bindingEngine;
    }

    public bind(context: IPlotParent): void {
        super.bind(context);

        this.useBaseUnit = this.selectionunit ? false : true;
        this._selectionunit = this.selectionunit;

        this.selectionChanged(this.selection);

        if (this.selectedRangesDisposable) {
            this.selectedRangesDisposable.dispose();
            this.selectedRangesDisposable = undefined;
        }

        this.selectedRangesDisposable = this.bindingEngine
            .collectionObserver(this.ranges)
            .subscribe(() => this.updateRangeRectangles());

        this.selectionDisposable = this.bindingEngine.collectionObserver(this.selection).subscribe(() => {
            this.selectionChanged(this.selection);
        });
    }

    public unbind(): void {
        if (this.selectedRangesDisposable) {
            this.selectedRangesDisposable.dispose();
            this.selectedRangesDisposable = undefined;
        }

        if (this.selectionDisposable) {
            this.selectionDisposable.dispose();
            this.selectionDisposable = undefined;
        }
    }

    public attached(): void {
        super.attached();

        this.plotParent.addInputEventListener("touchstart", this.touchDown);
        this.plotParent.addInputEventListener("touchmove", this.touchMove);
        this.plotParent.addInputEventListener("touchend", this.touchUp);
        this.plotParent.addInputEventListener("mouseenter", this.onMouseEnter);
        this.plotParent.addInputEventListener("mouseleave", this.onMouseLeave);
        this.plotParent.addInputEventListener("mousedown", this.onMouseDown);
        this.plotParent.addWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.addWindowInputEventListener("mouseup", this.onMouseUp);
        this.plotParent.addWindowInputEventListener("mouseleave", this.onMouseWindowLeave);
        this.plotParent.addInputEventListener("contextmenu", this.onContextMenu);
        this.plotParent.addWindowInputEventListener("keypress", this.onKeyPress);
    }

    public detached(): void {
        this.plotParent.removeInputEventListener("touchstart", this.touchDown);
        this.plotParent.removeInputEventListener("touchmove", this.touchMove);
        this.plotParent.removeInputEventListener("touchend", this.touchUp);
        this.plotParent.removeInputEventListener("mouseenter", this.onMouseEnter);
        this.plotParent.removeInputEventListener("mouseleave", this.onMouseLeave);
        this.plotParent.removeInputEventListener("mousedown", this.onMouseDown);
        this.plotParent.removeWindowInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.removeWindowInputEventListener("mouseup", this.onMouseUp);
        this.plotParent.removeWindowInputEventListener("mouseleave", this.onMouseWindowLeave);
        this.plotParent.removeInputEventListener("contextmenu", this.onContextMenu);
        this.plotParent.removeWindowInputEventListener("keypress", this.onKeyPress);

        super.detached();
    }

    //#endregion

    //#region IPlotPart

    public onMeasureMargin(): Margin {
        return this.isSelectionModeX ? new Margin(0, 0, 0, 15) : new Margin(15, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        super.onSize(size, margin);

        if (this.actualSize.width <= 0 || this.actualSize.height <= 0) return;

        this.viewBox = `${-this.margin.left} ${-this.margin.top} ${this.size.width} ${this.size.height}`;

        this.updateRangeRectangles();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>): void {
        super.onViewPortComputed(viewPort, viewPortBounds, units);

        this.updateSettings();
    }

    //#endregion

    //#region Change Handlers

    public selectionmodeChanged(newValue: string, oldValue: string): void {
        if (!this.isValidXYSelector(newValue) || newValue == "") {
            console.error("BinSelectionPart: Invalid selection mode");
            this.selectionmode = oldValue;
        }

        this.updateSettings();
    }

    public selectionChanged(newValue: SelectableRange[]): void {
        this.ranges = newValue ? newValue : [];
        this.updateRangeRectangles();
    }

    private selectionunitChanged(newValue: Unit) {
        this.useBaseUnit = newValue ? false : true;
        if (newValue) {
            this._selectionunit = newValue;
        }
        this.plotParent.requestMeasure(true);
    }

    //#endregion

    //#region Mouse Handlers

    private onDelete(rectangle: RangeRectangle) {
        // Find the correct rectangle and range and remove them
        const index = this.rectangles.indexOf(rectangle);
        if (index >= 0) {
            this.rectangles.splice(index, 1);
            this.ranges.splice(index, 1);
        }

        this.updateRangeRectangles();
        this.updateSelection();
    }

    // returns true if the event was handled
    private onSelectionStart(event: Event, x: number, y: number): boolean {
        if (event.eventPhase === event.BUBBLING_PHASE || this.blockMouseEvents) return false;

        this.mouseDown = true;
        this.selectionHasChanged = false;
        this.resizingPart = undefined;

        // Make sure the event doesn't bubble
        event.stopPropagation();

        // If the mouse is out of bounds, return undefined
        const value = this.viewToData(x, y);
        if (value === undefined) return false;

        const { range, rangePart } = this.getSelectableRange(x, y);

        if (range === undefined) {
            this.startNewRange(value);
            return true;
        }

        this.resizingPart = rangePart;
        this.currentRange = range;

        return true;
    }

    private startNewRange(dataValue: number) {
        this.selectionHasChanged = true;

        this.currentRange = new SelectableRange(
            this._selectionunit.fromUnit(
                this.sourceUnit,
                this.plotParent.roundToNearestBin(dataValue, this.selectionmode)
            ),
            this._selectionunit.fromUnit(
                this.sourceUnit,
                this.plotParent.roundToNearestBin(dataValue, this.selectionmode)
            )
        );

        this.currentRange.isSelected = true;

        if (this.singleSelection) {
            this.ranges.splice(0, this.ranges.length);
        } else if (!this.allowMultipleActiveRanges) {
            this.ranges.forEach((r) => (r.isSelected = false));
        }

        this.ranges.push(this.currentRange);
    }

    private getSelectableRange(
        x: number,
        y: number
    ): { range: SelectableRange; rangePart: "lower" | "center" | "upper" } {
        const value = this.viewToData(x, y);

        if (value === undefined) {
            return { range: undefined, rangePart: undefined };
        }

        //Add this.selectionBorderOffset in pixels on each side of the click in order to make borders selectable
        const clickMarginLow = this.viewToData(x - this.selectionBorderOffset, y - this.selectionBorderOffset);
        const clickMarginHigh = this.viewToData(x + this.selectionBorderOffset, y + this.selectionBorderOffset);

        // simplified version
        for (const range of this.ranges) {
            const rangeFrom = this.sourceUnit.fromUnit(this._selectionunit, range.from);
            const rangeTo = this.sourceUnit.fromUnit(this._selectionunit, range.to);

            //Clicked near the lower bound of this range
            if (
                rangeFrom >= Math.min(clickMarginLow, clickMarginHigh) &&
                rangeFrom <= Math.max(clickMarginLow, clickMarginHigh)
            ) {
                return { range: range, rangePart: "lower" };
            }

            //Clicked near the upper bound of this range
            if (
                rangeTo >= Math.min(clickMarginLow, clickMarginHigh) &&
                rangeTo <= Math.max(clickMarginLow, clickMarginHigh)
            ) {
                return { range: range, rangePart: "upper" };
            }

            //Clicked in this range
            if (rangeFrom <= value && rangeTo >= value) {
                return { range: range, rangePart: "center" };
            }
        }

        return { range: undefined, rangePart: undefined };
    }

    private onSelectionStop(): boolean {
        if (!this.mouseDown) return;

        if (!this.selectionHasChanged && this.selectable) {
            this.toggleRangeActive(this.currentRange);
        }

        this.updateSelection();

        this.mouseDown = false;
        this.currentRange = undefined;
        this.updateRangeRectangles();

        return true;
    }

    private toggleRangeActive(range: SelectableRange): void {
        range.isSelected = !range.isSelected;

        // If we don't allow multiple active ranges, deactivate all other ranges
        if (range.isSelected && !this.allowMultipleActiveRanges) {
            this.ranges.forEach((r) => {
                if (r !== range) r.isSelected = false;
            });
        }
    }

    private onSelectionMove(event: Event, x: number, y: number): void {
        // Check if we have all required fields set
        const hasAllRequiredFields =
            this.mouseOver &&
            this.size &&
            this.actualSize &&
            this.margin &&
            this.viewPort &&
            this.viewPort.width !== 0 &&
            this.viewPort.height !== 0;
        if (!hasAllRequiredFields || this.blockMouseEvents) return;

        this.currentValue = this.viewToData(x, y);

        this.lastMouseX = x;
        this.lastMouseY = y;

        this.setCursor(x, y);

        if (this.stepped) {
            this.showSteppedHoverLines(x, y);
        } else {
            this.showHoverLine();
        }

        if (this.mouseDown) {
            this.resize();
        }
        return;
    }

    // Shows a horizontal and vertical line for each graph path going to the point on the graph where the x mouse position is
    private showSteppedHoverLines(mouseX: number, mouseY: number) {
        if (!this.pathData || this.pathData.length == 0) return;

        const lines: RangeLine[] = [];
        const labelCircles: LabelCircle[] = [];

        const currentDataX = this.viewToDataX(mouseX);

        // Process each path separately
        this.pathData.forEach((path) => {
            // Find closest X value in this path
            const closestPoint = path.data.reduce((prev, curr) =>
                Math.abs(curr.x - currentDataX) < Math.abs(prev.x - currentDataX) ? curr : prev
            );

            const currentDataY = closestPoint.y;
            const currentSVGY = this.dataToSvgY(currentDataY);

            // Add circle label for this path
            labelCircles.push({
                circlePos: { x: mouseX, y: currentSVGY },
                labelValue: currentDataY,
            });

            // Add vertical line (this will overlap)
            lines.push({
                value: this.currentValue,
                x0: mouseX,
                x1: mouseX,
                y0: currentSVGY,
                y1: this.actualSize.height,
            });

            // Add horizontal line
            lines.push({
                value: this.viewToDataY(mouseY),
                y0: currentSVGY,
                y1: currentSVGY,
                x0: 0,
                x1: mouseX,
            });
        });

        this.lines = lines;
        this.labelCircles = labelCircles;
    }

    // Shows a horizontal or vertical line to mark where the user can place a selection
    private showHoverLine() {
        const lines: RangeLine[] = [];

        if (this.isSelectionModeX) {
            const xSvg = this.dataToSvgX(this.currentValue);
            lines.push({ value: this.currentValue, x0: xSvg, x1: xSvg, y0: 0, y1: this.actualSize.height });
        }

        if (this.isSelectionModeY) {
            const ySvg = this.dataToSvgY(this.currentValue);
            lines.push({ value: this.currentValue, y0: ySvg, y1: ySvg, x0: 0, x1: this.actualSize.width });
        }
        this.lines = lines;
    }

    private setCursor(x: number, y: number): void {
        const { range, rangePart } = this.getSelectableRange(x, y);
        let newCursor = "auto";
        if (rangePart === "lower" || rangePart === "upper") {
            newCursor = this.isSelectionModeX ? "e-resize" : "s-resize";
        } else if (rangePart === "center") {
            newCursor = "pointer";
        }
        if (this.currentCursor !== newCursor) {
            this.currentCursor = newCursor;
            this.plotParent.cursorClass = newCursor;
        }
    }

    private resize(): void {
        if (this.currentRange === undefined) return;

        // If mouse is down, add selection rectangle
        if (!this.resizingPart) {
            const newFrom = Math.min(
                this._selectionunit.fromUnit(this.sourceUnit, this.currentValue),
                this.currentRange.from
            );
            const newTo = Math.max(
                this._selectionunit.fromUnit(this.sourceUnit, this.currentValue),
                this.currentRange.to
            );

            // check if range has changed, for "click callback"
            if (newFrom !== this.currentRange.from || newTo !== this.currentRange.to) {
                this.selectionHasChanged = true;
            }

            // Assign
            this.currentRange.from = newFrom;
            this.currentRange.to = newTo;
        } else if (this.resizingPart === "lower") {
            // If mouse is down, resize selection rectangle
            let newFrom = this._selectionunit.fromUnit(this.sourceUnit, this.currentValue);

            //newFrom cannot be bigger than the upper bound of the range
            if (newFrom > this.currentRange.to) newFrom = this.currentRange.to;

            if (newFrom !== this.currentRange.from) {
                this.selectionHasChanged = true;
            }

            this.currentRange.from = newFrom;
        } else if (this.resizingPart === "upper") {
            // If mouse is down, resize selection rectangle
            let newTo = this._selectionunit.fromUnit(this.sourceUnit, this.currentValue);

            //newTo cannot be smaller than the lower bound of the range
            if (newTo < this.currentRange.from) newTo = this.currentRange.from;

            if (newTo !== this.currentRange.to) {
                this.selectionHasChanged = true;
            }
            this.currentRange.to = newTo;
        }

        if (RangeLogic.hasOverlap(this.ranges)) {
            // Merge ranges and stop the resizing operation
            this.ranges = RangeLogic.union(this.ranges, (a, b, merged) => {
                merged.isSelected = a.isSelected || b.isSelected;
            });
            this.onSelectionStop();
        }
        this.updateRangeRectangles();
    }

    private onMouseEnter = (): boolean => {
        if (!this.touchActive) this.mouseOver = true;

        return false;
    };

    private onMouseLeave = (): void => {
        // Handle leave
        this.mouseOver = false;
        this.lines = [];
        this.labelCircles = [];
    };

    private onMouseWindowLeave = (): void => {
        this.onMouseUp();
        this.onMouseLeave();
    };

    private onMouseMove = (event: MouseEvent): void => {
        if (!this.touchActive) {
            this.onSelectionMove(event, event.offsetX, event.offsetY);
        }
    };

    private onKeyPress = async (event: KeyboardEvent): Promise<void> => {
        if (event.key === "Enter") {
            await new Promise((f) => setTimeout(f, 300));

            this.onSelectionMove(event, this.lastMouseX, this.lastMouseY);
        }
    };

    private onMouseDown = (event: MouseEvent): boolean => {
        // Only trigger for normal mouse buttons
        if (event.eventPhase === event.BUBBLING_PHASE || (event.buttons & 0x1) === 0) {
            return false;
        }

        // Make sure the browser is not doing fancy stuff
        event.preventDefault();

        return this.onSelectionStart(event, event.offsetX, event.offsetY);
    };

    private onContextMenu = (event: PointerEvent): void => {
        this.onSelectionDelete(event, event.offsetX, event.offsetY);
    };

    private onSelectionDelete(event: Event, xvalue: number, yvalue: number): void {
        const x = this.viewToDataX(xvalue);
        const y = this.viewToDataY(yvalue);
        const value = this.isSelectionModeX ? x : this.isSelectionModeY ? y : undefined;
        if (value === undefined) return;

        for (let i = 0; i < this.ranges.length; i++) {
            const range = this.ranges[i];
            if (
                this.plotParent.roundToNearestBin(
                    this.sourceUnit.fromUnit(this._selectionunit, range.from),
                    this.selectionmode
                ) <= value &&
                this.plotParent.roundToNearestBin(
                    this.sourceUnit.fromUnit(this._selectionunit, range.to),
                    this.selectionmode
                ) >= value
            ) {
                this.ranges.splice(i, 1);
                break;
            }
        }
    }

    private onMouseUp = (): void => {
        this.onSelectionStop();
    };

    private touchDown = (event: TouchEvent): boolean => {
        event.preventDefault();

        // Highlight current touch, when only a single touch is detected
        if (event.touches.length == 1) {
            this.mouseOver = true;
            this.touchActive = true;

            const element = <HTMLElement>event.touches[0].target;

            return this.onSelectionStart(
                event,
                event.touches[0].pageX - element.getBoundingClientRect().left,
                event.touches[0].pageY - element.getBoundingClientRect().top
            );
        } else if (this.touchActive) {
            this.mouseOver = false;
            this.mouseDown = false;
            this.lines = [];
            this.touchActive = false;
            this.currentRange = undefined;
            this.ranges = [];
        }

        return true;
    };

    private touchMove = (event: TouchEvent): boolean => {
        event.preventDefault();

        if (event.touches.length == 1 && this.touchActive) {
            const element = <HTMLElement>event.touches[0].target;

            this.onSelectionMove(
                event,
                event.touches[0].pageX - element.getBoundingClientRect().left,
                event.touches[0].pageY - element.getBoundingClientRect().top
            );
        }

        return true;
    };

    private touchUp = (): boolean => {
        this.mouseOver = false;
        this.lines = [];

        if (this.touchActive) {
            this.onSelectionStop();
            this.touchActive = false;
        }

        return true;
    };

    //#endregion

    //#region Update Functions

    private updateSettings(): void {
        // Settings have been updated, redraw the rectangles
        this.updateRangeRectangles();
    }

    private updateRangeRectangles(): void {
        this.rectangles = [];

        // Check whether there is a selection mode and ranges
        if (!this.ranges || this.ranges.length == 0 || !this.selectionmode) {
            return;
        }

        // Guard against undefined units
        if (!this.sourceUnits || !this.sourceUnits.x || !this.sourceUnits.y) {
            return;
        }

        if (!this.actualSize || !this.viewPort) {
            return;
        }

        let rectangles: RangeRectangle[] = [];

        // Draw a rectangle for each of the selected ranges

        // This variable was added to aid selection with stepped graphs
        // Since we are offsetting from and to by a half, this also makes the selection on stepped graphs (which are centered around the exact unit value) work nicely
        // E.g.if we want to select step 3 (shown from 2.5 to 3.5) and 4 (shown from 3.5 to 4.5), our rectangle will, like the steps, look like 2.5 to 4.5
        // Since this is only applied to the rectangle, the text header of the selection still looks like [3,4] (including)
        const minRange = 1;

        if (this.selectionmode === "x") {
            rectangles = this.ranges.map((range) => {
                const from = this.stepped ? Math.round(range.from) - minRange / 2.0 : range.from;
                const x0 = this.dataToSvgX(this.sourceUnits.x.fromUnit(this._selectionunit, from));
                const to = this.stepped ? Math.round(range.to) + minRange / 2.0 : range.to;
                const x1 = this.dataToSvgX(this.sourceUnits.x.fromUnit(this._selectionunit, to));

                return new RangeRectangle(range, x0, 0, x1 - x0, this.actualSize.height);
            });
        }

        if (this.selectionmode === "y") {
            rectangles = this.ranges.map((range) => {
                // Y direction is the wrong way in svg so swap y0 and y1
                const from = this.stepped ? Math.round(range.from) - minRange / 2.0 : range.from;
                const y1 = this.dataToSvgY(this.sourceUnits.y.fromUnit(this._selectionunit, from));
                const to = this.stepped ? Math.round(range.to) + minRange / 2.0 : range.to;
                const y0 = this.dataToSvgY(this.sourceUnits.y.fromUnit(this._selectionunit, to));

                return new RangeRectangle(range, 0, y0, this.actualSize.width, y1 - y0);
            });
        }

        this.rectangles = rectangles;
    }

    private updateSelection(): void {
        if (!this.ranges) return;

        // Create a copy of the array of ranges
        const newSelection = <SelectableRange[]>[];

        for (let i = 0; i < this.ranges.length; i++) {
            const range = this.ranges[i];

            newSelection.push({
                from: this._selectionunit.fromUnit(
                    this.sourceUnit,
                    this.plotParent.roundToNearestBin(
                        this.sourceUnit.fromUnit(this._selectionunit, range.from),
                        this.selectionmode
                    )
                ),
                to: this._selectionunit.fromUnit(
                    this.sourceUnit,
                    this.plotParent.roundToNearestBin(
                        this.sourceUnit.fromUnit(this._selectionunit, range.to),
                        this.selectionmode
                    )
                ),
                isSelected: range.isSelected,
            });

            if (this.singleSelection) {
                break;
            }
        }

        // Replace selection without destroying references
        this.selection.splice(0, this.selection.length, ...newSelection);
        this.selectionUpdatedCallback(this.selection);
    }

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.plotElement) return undefined;

        const svgs = SVGNode.getSvgsInElement(this.plotElement);
        if (svgs.length != 1) return undefined; // Only 1 expected..

        const svg = svgs[0];

        // Find clip path in svg and give it a unique identifier
        const newClipPathId = this.clipPathId + "_preview";
        svg.updateWhere(
            (x) => x.attributes["id"] === this.clipPathId,
            (x) => (x.attributes["id"] = newClipPathId)
        );
        svg.updateWhere(
            (x) => Object.prototype.hasOwnProperty.call(x.attributes, "clip-path"),
            (x) => (x.attributes["clip-path"] = "url(#" + newClipPathId + ")")
        );

        return Promise.resolve(svg);
    }

    //#endregion

    //#region Helpers
    public getTooltipGroupTransform(line: RangeLine): string {
        if (!line) return "";

        if (this.hoverAxis === "x") {
            return `translate(${line.x0} ${this.size.height - BinSelectionPart.LabelHeight - BinSelectionPart.LabelMargin})`;
        } else {
            // Y hover — follow the line vertically
            return `translate(0 ${line.y0})`;
        }
    }

    public getTooltipTextPosition(line: RangeLine): { x: number; y: number } {
        if (!line) return { x: 0, y: 0 };

        if (this.hoverAxis === "x") {
            return { x: this.computeSelectedValueX(line), y: BinSelectionPart.LabelHeight / 2 };
        } else {
            return { x: this.computeSelectedValueY(line), y: -5 }; // relative to rotated polygon
        }
    }

    public getTooltipBackgroundPath(line: RangeLine): string {
        return this.computeSelectedValueBackgroundPath(line);
    }

    private getXSelectionHeaders(): RangeRectangleHeader[] {
        const rects = <RangeRectangleHeader[]>[];

        // For each selection rectangle determine the position of the header
        for (const selectionRect of this.rectangles) {
            let x = this.getToValueX(selectionRect);

            // Start with y = 0 and check if the space is free
            let y = 0;
            while (y < this.maxStackedHeight && !this.rectangleFitsX(x, y, rects)) {
                y += this.labelHeightWithMargin;
            }

            // If the space is free, add the header
            if (y < this.maxStackedHeight) {
                rects.push(
                    new RangeRectangleHeaderX(
                        x,
                        y,
                        BinSelectionPart.LabelWidth,
                        BinSelectionPart.LabelHeight,
                        true,
                        x + BinSelectionPart.LabelWidth * 0.5 > selectionRect.x0 + selectionRect.width,
                        selectionRect,
                        selectionRect.range.to.toFixed(0)
                    )
                );
            }

            if (Math.round(selectionRect.range.from) != Math.round(selectionRect.range.to)) {
                x = this.getFromValueX(selectionRect);
                y = Math.floor(this.actualSize.height - BinSelectionPart.LabelHeight);
                const minY = this.actualSize.height - this.maxStackedHeight;

                while (y >= minY && !this.rectangleFitsX(x, y, rects)) {
                    y -= this.labelHeightWithMargin;
                }

                if (y >= minY) {
                    rects.push(
                        new RangeRectangleHeaderX(
                            x,
                            y,
                            BinSelectionPart.LabelWidth,
                            BinSelectionPart.LabelHeight,
                            false,
                            x < selectionRect.x0,
                            selectionRect,
                            selectionRect.range.from.toFixed(0)
                        )
                    );
                }
            }
        }

        return rects;
    }

    private getYSelectionHeaders(): RangeRectangleHeader[] {
        const rects = <RangeRectangleHeader[]>[];

        // In the Y direction we dont have much space, only show one of the labels if there are collisions
        for (const selectionRect of this.rectangles) {
            let y = this.getToValueY(selectionRect);
            let x = this.actualSize.width - BinSelectionPart.LabelWidth;
            const minY = y - BinSelectionPart.LabelHeight * 1.5;

            while (y > minY && !this.rectangleFitsY(x, y, rects)) {
                y -= BinSelectionPart.LabelHeight;
            }

            if (y > minY) {
                rects.push(
                    new RangeRectangleHeaderY(
                        x,
                        y,
                        BinSelectionPart.LabelWidth,
                        BinSelectionPart.LabelHeight,
                        true,
                        y < selectionRect.y0,
                        selectionRect,
                        selectionRect.range.to.toFixed(0)
                    )
                );
            }

            if (Math.round(selectionRect.range.from) != Math.round(selectionRect.range.to)) {
                y = this.getFromValueY(selectionRect);
                x = 0;

                if (!this.rectangleFitsY(x, y, rects)) continue;

                rects.push(
                    new RangeRectangleHeaderY(
                        x,
                        y - BinSelectionPart.LabelHeight,
                        BinSelectionPart.LabelWidth,
                        BinSelectionPart.LabelHeight,
                        false,
                        y >= selectionRect.y0 + selectionRect.height + BinSelectionPart.LabelHeight * 0.5,
                        selectionRect,
                        selectionRect.range.from.toFixed(0)
                    )
                );
            }
        }

        return rects;
    }

    private rectangleFitsX(x: number, y: number, rects: RangeRectangleHeader[]): boolean {
        for (const rect of rects) {
            // Collision
            if (rect.y === y && rect.x < x + BinSelectionPart.LabelWidth && rect.x + BinSelectionPart.LabelWidth > x) {
                return false;
            }
        }
        return true;
    }

    private rectangleFitsY(x: number, y: number, rects: RangeRectangleHeader[]): boolean {
        for (const rect of rects) {
            // Collision
            if (
                rect.x === x &&
                rect.y < y + BinSelectionPart.LabelHeight &&
                rect.y + BinSelectionPart.LabelHeight > y
            ) {
                return false;
            }
        }
        return true;
    }

    private getToValueX(selectedRectangle: RangeRectangle) {
        let start = -BinSelectionPart.LabelWidth;

        if (selectedRectangle.width + selectedRectangle.x0 + start < 0) {
            start += BinSelectionPart.LabelWidth;
        }

        return selectedRectangle.x0 + selectedRectangle.width + start;
    }

    private getFromValueX(selectedRectangle: RangeRectangle) {
        let start = 0;

        if (this.isSelectionModeX) {
            if (
                selectedRectangle.x0 + start + BinSelectionPart.LabelWidth - BinSelectionPart.LabelHeight >
                this.actualSize.width
            ) {
                start -= BinSelectionPart.LabelWidth - BinSelectionPart.LabelHeight;
            }
        }

        return selectedRectangle.x0 + start;
    }

    private getToValueY(selectedRectangle: RangeRectangle) {
        let start = 0;
        if (start + selectedRectangle.y0 + BinSelectionPart.LabelHeight > this.actualSize.height) {
            start -= BinSelectionPart.LabelHeight;
        }

        return selectedRectangle.y0 + start;
    }

    private getFromValueY(selectedRectangle: RangeRectangle) {
        let start = 0;

        if (start + selectedRectangle.y0 + selectedRectangle.height - BinSelectionPart.LabelHeight < 0) {
            start += BinSelectionPart.LabelHeight;
        }

        return selectedRectangle.y0 + selectedRectangle.height + start;
    }

    private computeSelectedValueBackgroundPath(selectedLine: RangeLine): string {
        let start = -this.valueBackgroundWidth / 2;
        let end = this.valueBackgroundWidth / 2;

        const arrowSize = 10;
        let arrowStart = -arrowSize / 2;
        let arrowEnd = arrowSize / 2;

        if (this.isSelectionModeX) {
            // Prevent fliepeltje from going over the left
            if (selectedLine.x0 + start < 0) {
                start = -selectedLine.x0;
                end = start + this.valueBackgroundWidth;
            }

            // Prevent fliepeltje from going over the right
            if (selectedLine.x0 + end > this.actualSize.width) {
                end = this.actualSize.width - selectedLine.x0;
                start = end - this.valueBackgroundWidth;
            }

            // Prevent arrow from going over the left
            if (selectedLine.x0 + arrowStart < 0) {
                arrowStart = -selectedLine.x0;
                arrowEnd = arrowStart + arrowSize;
            }

            // Prevent arrow from going over the right
            if (selectedLine.x0 + arrowEnd > this.actualSize.width) {
                arrowEnd = this.actualSize.width - selectedLine.x0;
                arrowStart = arrowEnd - arrowSize;
            }

            const tooltipBottom = BinSelectionPart.LabelHeight;
            const tooltipTop = 0;
            const arrowY = -BinSelectionPart.LabelMargin;

            return [
                `${start}, ${tooltipBottom}`,
                `${start}, ${tooltipTop}`,
                `${arrowStart}, ${tooltipTop}`,
                `0, ${arrowY}`,
                `${arrowEnd}, ${tooltipTop}`,
                `${end}, ${tooltipTop}`,
                `${end}, ${tooltipBottom}`,
            ].join(" ");
        } else if (this.isSelectionModeY) {
            // Prevent fliepeltje from going over the top
            if (selectedLine.y0 + start < 0) {
                start = -selectedLine.y0;
                end = start + this.valueBackgroundWidth;
            }

            // Prevent fliepeltje from going below the bottom
            if (selectedLine.y0 + end > this.actualSize.height) {
                end = this.actualSize.height - selectedLine.y0;
                start = end - this.valueBackgroundWidth;
            }

            // Prevent arrow from going over the top
            if (selectedLine.y0 + arrowStart < 0) {
                arrowStart = -selectedLine.y0;
                arrowEnd = arrowStart + arrowSize;
            }

            // Prevent arrow from going below the bottom
            if (selectedLine.y0 + arrowEnd > this.actualSize.height) {
                arrowEnd = this.actualSize.height - selectedLine.y0;
                arrowStart = arrowEnd - arrowSize;
            }

            return [
                `-15, ${start}`,
                `-1, ${start}`,
                `-1, ${arrowStart}`,
                "5, 0",
                `-1, ${arrowEnd}`,
                `-1, ${end}`,
                `-15, ${end}`,
            ].join(" ");
        }
    }

    private computeSelectedValueX(selectedLine: RangeLine): number {
        let start = -this.valueBackgroundWidth / 2;
        let end = this.valueBackgroundWidth / 2;

        // Prevent fliepeltje from going over the left
        if (selectedLine.x0 + start < 0) {
            start = -selectedLine.x0;
            end = start + this.valueBackgroundWidth;
        }

        // Prevent fliepeltje from going over the right
        if (selectedLine.x0 + end > this.actualSize.width) {
            end = this.actualSize.width - selectedLine.x0;
            start = end - this.valueBackgroundWidth;
        }

        return start + (end - start) / 2;
    }

    private computeSelectedValueY(selectedLine: RangeLine): number {
        let start = -this.valueBackgroundWidth / 2;
        let end = this.valueBackgroundWidth / 2;

        // Prevent fliepeltje from going over the top
        if (selectedLine.y0 + start < 0) {
            start = -selectedLine.y0;
            end = start + this.valueBackgroundWidth;
        }

        // Prevent fliepeltje from going below the bottom
        if (selectedLine.y0 + end > this.actualSize.height) {
            end = this.actualSize.height - selectedLine.y0;
            start = end - this.valueBackgroundWidth;
        }

        return -(start + (end - start) / 2);
    }

    private isValidXYSelector(value: string) {
        if (value === null || value === undefined || typeof value !== "string") return false;

        const vv = value.toLowerCase();

        if (vv === "" || vv === "y") return true;

        return false;
    }

    private get isSelectionModeX(): boolean {
        // Just compare, only x and y are supported
        return this.selectionmode && typeof this.selectionmode === "string" && this.selectionmode.toLowerCase() === "x";
    }

    private get isSelectionModeY(): boolean {
        // Just compare, only x and y are supported
        return this.selectionmode && typeof this.selectionmode === "string" && this.selectionmode.toLowerCase() === "y";
    }

    private get sourceUnit(): Unit {
        if (!this.sourceUnits) return this._selectionunit;

        return this.isSelectionModeX ? this.sourceUnits.x : this.sourceUnits.y;
    }

    private viewToDataX(x: number): number {
        if (x < 0) x = 0;
        if (x > this.actualSize.width) x = this.actualSize.width;

        return (x / this.actualSize.width) * this.viewPort.width + this.viewPort.x;
    }

    private viewToDataY(y: number): number {
        if (y < 0) y = 0;
        if (y > this.actualSize.height) y = this.actualSize.height;

        return (1 - y / this.actualSize.height) * this.viewPort.height + this.viewPort.y;
    }

    private viewToData(x: number, y: number) {
        if (this.isSelectionModeX) {
            return this.viewToDataX(x);
        } else if (this.isSelectionModeY) {
            return this.viewToDataY(y);
        }
        return undefined;
    }

    private dataToSvgX(x: number): number {
        return ((x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width;
    }

    private dataToSvgY(y: number): number {
        return (1 - (y - this.viewPort.y) / this.viewPort.height) * this.actualSize.height;
    }

    //#endregion
}
